import React from 'react';
import {
  Box,
  HStack,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '@chakra-ui/react';
import { Icon } from 'src/components/atoms/Icon';
import { Checkbox } from 'src/components/atoms/Checkbox';
import { PaginationControls } from 'src/components/molecules/PaginationControls';
import { ManagedUser } from 'src/api/users';
import { UserStatusBadge } from '../UserStatusBadge';
import { UserRoleBadge } from '../UserRoleBadge';

type UsersTableProps = {
  users: ManagedUser[];
  selectedIds: Set<string>;
  onSelectionChange: (ids: Set<string>) => void;
  onEdit: (user: ManagedUser) => void;
  onDelete: (user: ManagedUser) => void;
  onResendInvite: (user: ManagedUser) => void;
  // Pagination props
  totalUsers: number;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  onShowMore: () => void;
  hasMore: boolean;
};

export const UsersTable: React.FC<UsersTableProps> = ({
  users,
  selectedIds,
  onSelectionChange,
  onEdit,
  onDelete,
  onResendInvite,
  totalUsers,
  pageSize,
  onPageSizeChange,
  onShowMore,
  hasMore,
}) => {
  const allSelected = users.length > 0 && selectedIds.size === users.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < users.length;

  const handleSelectAll = () => {
    if (allSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(users.map((u) => u.id)));
    }
  };

  const handleSelectOne = (userId: string) => {
    const newSelection = new Set(selectedIds);
    if (newSelection.has(userId)) {
      newSelection.delete(userId);
    } else {
      newSelection.add(userId);
    }
    onSelectionChange(newSelection);
  };

  return (
    <Box
      position="relative"
      maxHeight="500px"
      overflowY="auto"
      width="100%"
      border="1px solid"
      borderColor="border.layout"
      borderRadius="md">
      <Table variant="simple" width="100%">
        <Thead position="sticky" top={0} zIndex={1} bg="surface.control">
          <Tr
            bg="surface.control"
            borderBottom="1px"
            borderColor="border.layout">
            <Th px={4} py={4} w="40px" verticalAlign="middle">
              <Checkbox
                isChecked={allSelected}
                isIndeterminate={someSelected}
                onChange={handleSelectAll}
              />
            </Th>
            <Th
              color="text.darkGray"
              fontWeight="semibold"
              fontSize="sm"
              px={4}
              py={4}
              verticalAlign="middle">
              Full Name
            </Th>
            <Th
              color="text.darkGray"
              fontWeight="semibold"
              fontSize="sm"
              px={4}
              py={4}
              verticalAlign="middle">
              Email
            </Th>
            <Th
              color="text.darkGray"
              fontWeight="semibold"
              fontSize="sm"
              px={4}
              py={4}
              verticalAlign="middle">
              Role
            </Th>
            <Th
              color="text.darkGray"
              fontWeight="semibold"
              fontSize="sm"
              px={4}
              py={4}
              verticalAlign="middle">
              Status
            </Th>
            <Th
              color="text.darkGray"
              fontWeight="semibold"
              fontSize="sm"
              px={4}
              py={4}
              verticalAlign="middle">
              Manage
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {users.length === 0 ? (
            <Tr>
              <Td colSpan={6} px={4} py={8}>
                <Text textAlign="center" color="gray.500">
                  No users found
                </Text>
              </Td>
            </Tr>
          ) : (
            users.map((user) => (
              <Tr
                key={user.id}
                borderBottom="1px"
                borderColor="border.layout"
                _hover={{ bg: 'surface.hover' }}>
                <Td px={4} py={4}>
                  <Checkbox
                    isChecked={selectedIds.has(user.id)}
                    onChange={() => handleSelectOne(user.id)}
                  />
                </Td>
                <Td fontSize="md" color="text.title" px={4} py={4}>
                  <Text fontWeight={user.displayName ? 'medium' : 'normal'}>
                    {user.displayName || '-'}
                  </Text>
                </Td>
                <Td fontSize="md" color="text.title" px={4} py={4}>
                  {user.email}
                </Td>
                <Td px={4} py={4}>
                  <UserRoleBadge role={user.role} />
                </Td>
                <Td px={4} py={4}>
                  <UserStatusBadge status={user.status} />
                </Td>
                <Td px={4} py={4}>
                  <HStack spacing={2}>
                    <Icon
                      iconName="pencil"
                      boxSize={5}
                      color="text.muted"
                      cursor="pointer"
                      onClick={() => onEdit(user)}
                      _hover={{ color: 'icon.green' }}
                    />
                    {user.status === 'pending' && (
                      <Icon
                        iconName="refresh"
                        boxSize={5}
                        color="text.muted"
                        cursor="pointer"
                        onClick={() => onResendInvite(user)}
                        _hover={{ color: 'blue.500' }}
                      />
                    )}
                    <Icon
                      iconName="trash"
                      boxSize={5}
                      color="text.muted"
                      cursor="pointer"
                      onClick={() => onDelete(user)}
                      _hover={{ color: 'red.500' }}
                    />
                  </HStack>
                </Td>
              </Tr>
            ))
          )}
        </Tbody>
      </Table>

      {/* Pagination */}
      {totalUsers > 0 && (
        <Box position="sticky" bottom={3} width="fit-content" mx="auto">
          <PaginationControls
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
            totalItems={totalUsers}
            displayedCount={users.length}
            onShowMore={onShowMore}
            hasMore={hasMore}
            itemLabel="Users"
          />
        </Box>
      )}
    </Box>
  );
};
